/** Data freshness and the privacy statement that has to sit beside it.
 *
 *  Since September 2021 Microsoft 365 conceals user names in usage reports by
 *  default, tenant-wide — and that applies to the Graph API, not only the admin
 *  centre. When names are concealed the identifiers Microsoft returns are
 *  pseudonymous tokens that are **not stable between runs**, so they cannot be
 *  joined back to a directory user. Saying so plainly is the difference between
 *  a board people trust and one they quietly stop believing. */

import { EyeOff, Icon, Info } from "@norican/icons";
import { Alert, Card, Freshness } from "@norican/react";

interface DataFreshnessProps {
  configured: boolean;
  statusDetail: string;
  namesConcealed: boolean;
  generatedAt: string | null | undefined;
}

/** The backend rebuilds its snapshot every 15 minutes; flag anything older. */
const STALE_AFTER_MS = 15 * 60_000;

export function DataFreshness({
  configured,
  statusDetail,
  namesConcealed,
  generatedAt,
}: DataFreshnessProps) {
  return (
    <Card variant="subtle">
      <Card.Body>
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Icon icon={Info} size="sm" tone="info" />
              <span className="text-fg text-sm font-medium">Data source</span>
            </div>
            {generatedAt && (
              <Freshness label="Ingested" since={generatedAt} staleAfter={STALE_AFTER_MS} />
            )}
          </div>

          <p className="text-fg-muted text-sm">{statusDetail}</p>

          {configured && namesConcealed && (
            <Alert
              tone="info"
              title="Usage reports are pseudonymised for this tenant"
              icon={<Icon icon={EyeOff} size="sm" />}
            >
              Microsoft 365 conceals user names in usage reports by default, and the setting applies
              to the Graph API as well as the admin centre. The identifiers it returns are
              pseudonymous and change between runs, so they cannot be joined to a directory account
              — per-user workload panels are suppressed rather than shown against unstable names.
              Seat counts, which come from the directory rather than the reports, are unaffected. An
              administrator can change this in{" "}
              <span className="font-medium">
                Microsoft 365 admin centre → Settings → Org settings → Reports
              </span>
              .
            </Alert>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
